/* eslint-disable react/display-name */
import { breaks as pluginBreaks } from '@tarslib/bytemd-plugin-breaks';
import pluginFrontMatter from '@tarslib/bytemd-plugin-frontmatter';
import { pluginGfmViewer } from '@tarslib/bytemd-plugin-gfm';
import { highlight } from '@tarslib/bytemd-plugin-highlight';
import { pluginViewerMath } from '@tarslib/bytemd-plugin-math';
import pluginMediumZoom from '@tarslib/bytemd-plugin-medium-zoom';
import { pluginViewerMermaid } from '@tarslib/bytemd-plugin-mermaid';
import type { BytemdSafeMdxViewerPlugin } from '@tarslib/bytemd-processor';
import type { BytemdSafeMdxViewerProps } from '@tarslib/bytemd-react';
import { SafeMdxViewer } from '@tarslib/bytemd-react';
import type { ContainerDirective } from '@tarslib/plugin-remark-directive';
import pluginRemarkDirective from '@tarslib/plugin-remark-directive';
import usePersistFn from 'ahooks/lib/usePersistFn';
// import 'core-js/modules/es.string.match-all';
import { AppTheme } from '@/shared/constant/AppTheme';
import { Alert } from 'aui';
import GithubSlugger from 'github-slugger';
import 'highlight.js/styles/github-dark.css';
import type { PhrasingContent } from 'mdast';
import 'mdast-util-math';
import 'mdast-util-mdx';
import 'mdast-util-mdx-jsx';
import React, { useCallback, useMemo, useState } from 'react';
import ReactDOM from 'react-dom';
import rehypeSlug from 'rehype-slug';
import pluginViewerInsertIcon from '../MarkdownEditorPlugin/plugin-viewer-insert-icon';
import { MarkdownAccordion } from './Components/Accordion/Accordion';
import { MarkdownAccordionGroup } from './Components/Accordion/AccordionGroup';
import { Admonition } from './Components/Admonition';
import { AdmonitionConfig } from './Components/AdmonitionConfig';
import { MarkdownCard } from './Components/Card/Card';
import { MarkdownCardGroup } from './Components/Card/CardGroup';
import { CodeHighLight } from './Components/CodeHighLight';
import { MarkdownColumn } from './Components/Columns/Column';
import { MarkdownColumns } from './Components/Columns/Columns';
import { MarkdownContainerBox } from './Components/ContainerBox';
import { MarkdownCopyToClipboard } from './Components/CopyToClipboard/CopyToClipboard';
import { DataSchemaViewerFactory } from './Components/DataSchema/DataSchemaViewer';
import { MarkdownDivider } from './Components/Divider';
import { MarkdownEmbed } from './Components/Embed';
import { MarkdownFrame } from './Components/Frame/Frame';
import { MarkdownCol } from './Components/Grid/Col';
import { MarkdownRow } from './Components/Grid/Row';
import { HighLight } from './Components/HighLight';
import { MarkdownIcon } from './Components/Icon';
import { MarkdownIconComponentForText } from './Components/Icon/parser';
import SupportIconPropsNode from './Components/Icon/SupportIconPropsNode';
import { MarkdownImageBackground } from './Components/ImageBackground/ImageBackground';
import MarkdownList from './Components/List/MarkdownList';
import MarkdownListItem from './Components/List/MarkdownListItem';
import { Noop } from './Components/Noop';
import { MarkdownStep } from './Components/Steps/Step';
import { MarkdownSteps } from './Components/Steps/Steps';
import { MarkdownTab } from './Components/Tabs/Tab';
import { MarkdownTabItem } from './Components/Tabs/TabItem';
import { MarkdownTabs } from './Components/Tabs/Tabs';
import { MarkdownTipBad } from './Components/Tip/TipBad';
import { MarkdownTipGood } from './Components/Tip/TipGood';
import { MarkdownTipInfo } from './Components/Tip/TipInfo';
import { MarkdownTooltip } from './Components/Tooltip';
import type { MdxExternalComponent } from './Components/type';
import { MarkdownVideo } from './Components/Video/Video';
import { MarkdownOpenExternalLinkCtx } from './ctx/OpenExternalLinkCtx';
import { TagA } from './CustomTag/TagA';
import { TagHeadingFactory } from './CustomTag/TagHeadingFactory';
import { TagImage } from './CustomTag/TagImage';
import { TagP } from './CustomTag/TagP';
import './index.less';
import './markdown.less';
import { MarkdownSafeMdxViewerAppLowerNameContext } from './MarkdownSafeMdxViewerAppLowerNameContext';
import { MarkdownSafeMdxViewerLocaleContext } from './MarkdownSafeMdxViewerLocaleContext';
import { pluginAutoLinkHeadings } from './plugins/plugin-auto-link-headings';
import pluginOpenExternalLink from './plugins/plugin-external-link';
import pluginImageAddLoading from './plugins/plugin-image-add-loading';
import pluginImageSrcAddParams from './plugins/plugin-image-src-add-params';
import pluginImageSrcPrefix from './plugins/plugin-image-src-prefix';
import remarkDirectiveExtension from './plugins/remark-directive-extension';
import { isValidAdmonitionTag } from './plugins/remark-directive-extension-lib/remark-admonition';
import { isSupportHighlightType } from './plugins/remark-directive-extension-lib/remark-highlight';
import { usePropsSlugId } from './slugId';
// 将一切的 plugin 全部内置，只用简单（序列化成本不高）的配置控制开启或者关闭，
// 便于将计算密集的算法(mdast计算过程，所以很多 remark相关的逻辑未来都会优化性能）挪动到 worker 的优化。
// Viewer 最理想是只接受 jsx-json / html string
// text - (remark) -> mdast - () -> jsx
const isPdv = IS_PDV;
export interface BusinessMarkdownSafeMdxViewerProps {
  value?: string;
  renderExternalLink: (url: string) => string;
  openExternalLink?: (url: string | undefined, e: MouseEvent) => void;
  // heading id prefix
  slugIdPrefix?: string;
  // 以下都是与mdast产生有关的扩展插件以及配置
  enableHeadingSlugId?: boolean;
  enableHeadingAutoLink?: boolean;
  imageReplaceHostOptions?: {
    searchHostname: string[];
    replaceHostname: string;
    customDomainRequestPath?: string;
    isEnterpriseEdition?: boolean;
  };
  imageSrcAddParams?: Parameters<typeof pluginImageSrcAddParams>[0];
  showErrorMsg?: boolean;

  locale?: string;
  enableDataSchema?: boolean;
  // 默认为 true，但是 app 内是不支持的
  isSupportHash?: boolean;

  inDocView?: boolean;

  appLowerName: string;
  theme: AppTheme;
}

function Viewer({
  value: viewerValue = '',
  renderExternalLink,
  openExternalLink = undefined,
  slugIdPrefix: _slugIdPrefix,
  enableHeadingSlugId = false,
  enableHeadingAutoLink = false,
  imageReplaceHostOptions = undefined,
  imageSrcAddParams = undefined,
  enableDataSchema = false,
  showErrorMsg = false,
  locale = 'en-US',
  isSupportHash = true,
  inDocView = false,
  appLowerName,
  theme,
}: BusinessMarkdownSafeMdxViewerProps) {
  const slugIdPrefix = usePropsSlugId(_slugIdPrefix);
  const [iconElement, setIconElement] = useState<
    [
      HTMLElement,
      {
        icon: string;
      },
    ][]
  >([]);

  const innerOpenExternalLink = usePersistFn((url: string | undefined, e: MouseEvent) => {
    if (url?.startsWith('mailto:')) {
      return;
    }
    openExternalLink?.(url, e);
  });

  const DataSchemaViewer = useMemo(() => {
    return DataSchemaViewerFactory({ locale, appLowerName, theme });
  }, [locale, appLowerName, theme]);
  const ExternalComponent = useMemo(() => {
    return enableDataSchema ? DataSchemaViewer : Noop;
  }, [enableDataSchema, DataSchemaViewer]);

  const ExternalComponentMap: Record<string, MdxExternalComponent> = useMemo(() => {
    return {
      Icon: MarkdownIcon,
      Tabs: MarkdownTabs,
      Tab: MarkdownTab,
      TabItem: MarkdownTabItem,
      DataSchema: ExternalComponent,
      Accordion: MarkdownAccordion,
      AccordionGroup: MarkdownAccordionGroup,
      Tooltip: MarkdownTooltip,
      Video: MarkdownVideo,
      Card: MarkdownCard,
      CardGroup: MarkdownCardGroup,
      Column: MarkdownColumn,
      Columns: MarkdownColumns,
      Frame: MarkdownFrame,
      Steps: MarkdownSteps,
      Step: MarkdownStep,
      CopyToClipboard: MarkdownCopyToClipboard,
      TipBad: MarkdownTipBad,
      TipGood: MarkdownTipGood,
      TipInfo: MarkdownTipInfo,
      Divider: MarkdownDivider,
      Row: MarkdownRow,
      Col: MarkdownCol,
      Container: MarkdownContainerBox,
      Background: MarkdownImageBackground,
      Embed: MarkdownEmbed,
      ...MarkdownIconComponentForText,
    };
  }, [ExternalComponent]) as any;

  const hasBlockExternalComponent = useMemo(() => {
    const FilterInlineExternalComponent = Object.values(ExternalComponentMap).filter(
      (Component) => {
        return Component.inline !== true;
      },
    );
    return (child: any) => {
      return FilterInlineExternalComponent.includes(child);
    };
  }, [ExternalComponentMap]);

  const sanitizeFn: BytemdSafeMdxViewerProps['sanitizeFn'] = useCallback(
    (schema) => {
      const nextSchema = schema;
      const attributes = nextSchema.attributes || (nextSchema.attributes = {});

      attributes['*']?.push('style');
      attributes['*']?.push('className');
      nextSchema.clobberPrefix = '';
      attributes.div ??= [];
      attributes.div.push('data*');
      attributes.span ??= [];
      attributes.span.push('data*');
      attributes.p ??= [];
      attributes.p.push('data*');
      attributes.a ??= [];
      attributes.a.push('data*');
      attributes.code ??= [];
      attributes.code.push('className');

      // 私有化支持 iframe
      if (isPdv) {
        nextSchema.tagNames?.push('iframe');
        attributes.iframe = [
          'allow',
          'height',
          'width',
          'referrerPolicy',
          'sandbox',
          'src',
          'width',
          'style',
          'scrolling',
          'border',
          'frameBorder',
          // 'frameborder',
          // 'framespacing',
          'frameSpacing',
          'allowFullScreen',
          // 'allowfullscreen',
        ];
      }

      nextSchema.tagNames ??= [];
      Object.entries(ExternalComponentMap).forEach((ExternalComponentName) => {
        const [name, fn] = ExternalComponentName;
        nextSchema.tagNames?.push(name);
        attributes[name] = fn.attributes ?? [];
      });
      return nextSchema;
    },
    [ExternalComponentMap],
  );

  // openExternalLink
  const customComponent = useMemo(() => {
    const TagH1 = TagHeadingFactory('h1', inDocView);
    const TagH2 = TagHeadingFactory('h2', inDocView);
    const TagH3 = TagHeadingFactory('h3', inDocView);
    const TagH4 = TagHeadingFactory('h4', inDocView);
    const TagH5 = TagHeadingFactory('h5', inDocView);
    const TagH6 = TagHeadingFactory('h6', inDocView);
    return {
      a: TagA,
      img: TagImage,
      ...ExternalComponentMap,
      break: 'br' as any,
      p: TagP,
      h1: TagH1,
      h2: TagH2,
      h3: TagH3,
      h4: TagH4,
      h5: TagH5,
      h6: TagH6,
    };
  }, [ExternalComponentMap, inDocView]);

  const enableImageReplaceHost = !!imageReplaceHostOptions;
  const enableImageSrcAddParams = !!imageSrcAddParams;
  const mermaidPlugin = useMemo(() => {
    return pluginViewerMermaid(<></>, {
      theme: theme !== AppTheme.Dark ? 'default' : 'dark',
    });
  }, [theme]);
  // 移除了 pluginHTMLAttr 的支持
  const viewPlugin = useMemo(() => {
    return [
      {
        mdxHandlerList: ({ childrenFactory, ctx }) => {
          return (input) => {
            if ('res' in input) return input;
            const node = input.source;
            if (node?.type === 'list') {
              return {
                res: (
                  <MarkdownList
                    ordered={node.ordered}
                    start={node.start}
                    {...ctx.calcRequireIndexProps(input)}
                  >
                    {childrenFactory(node)}
                  </MarkdownList>
                ),
              };
            }
            if (node?.type === 'listItem') {
              return {
                res: (
                  <MarkdownListItem checked={node.checked} {...ctx.calcRequireIndexProps(input)}>
                    {childrenFactory(node)}
                  </MarkdownListItem>
                ),
              };
            }
            return input;
          };
        },
      },
      pluginGfmViewer(),
      pluginViewerMath({
        katexOptions: {
          output: 'html',
        },
        useInnerCss: async () => {
          await import('katex/dist/katex.min.css');
        },
        renderError(_error) {
          return <></>;
        },
      }),
      mermaidPlugin,
      highlight({
        HighLightComponent: CodeHighLight,
      }),
      pluginFrontMatter(),

      // text
      pluginViewerInsertIcon,
      pluginBreaks(),

      pluginMediumZoom(), // 这个需要使用 SafeMdxViewer 的 customComponent 代理 image 组件渲染
      pluginOpenExternalLink({
        // 这个需要使用 SafeMdxViewer 的 customComponent 代理 a 组件渲染, 现在在 customComponent 中的 a
        handleOnClick: innerOpenExternalLink,
        slugIdPrefix,
        enableHeadingSlugId,
        isSupportHash,
      }),
      //
      pluginRemarkDirective([
        {
          remark: ((processor) => processor.use(remarkDirectiveExtension, locale)) as NonNullable<
            BytemdSafeMdxViewerPlugin['remark']
          >,
          mdxHandlerList: (options) => {
            return (input) => {
              if ('res' in input) return input;
              const node = input.source as unknown as ContainerDirective;
              if (node.type === 'containerDirective') {
                // 只能支持外部渲染
                const sourceName = node.name.trim().split(' ');
                const nodeName = sourceName.shift() as string;
                if (isValidAdmonitionTag(nodeName)) {
                  const firstChild = node.children[0] as any;
                  const firstChildIsDirectiveLabel = firstChild?.data?.directiveLabel === true;
                  const children = firstChildIsDirectiveLabel
                    ? node.children.slice(1)
                    : node.children;
                  node.children = children;

                  const directionTitle = (() => {
                    if (firstChildIsDirectiveLabel) {
                      if (
                        !firstChild.children ||
                        !Array.isArray(firstChild.children) ||
                        firstChild.children.length === 0
                      ) {
                        // empty
                        return '';
                      }
                      return options.childrenFactory(firstChild as any);
                    }
                    return undefined;
                  })();
                  const attributeTitle = node?.attributes?.title;
                  const attributeIcon = node.attributes?.icon;
                  const attributeIconColor = node.attributes?.iconColor ?? undefined;
                  const customName = sourceName.join(' ');
                  const customTitle = attributeTitle ?? directionTitle;

                  return {
                    res: (
                      <Admonition
                        tagName={nodeName}
                        customName={customName}
                        customTitle={customTitle}
                        locale={locale}
                        customIcon={
                          attributeIcon ? (
                            <span className="item-center mr-2 inline-flex justify-center">
                              <SupportIconPropsNode
                                icon={attributeIcon}
                                iconColor={attributeIconColor}
                              />
                            </span>
                          ) : null
                        }
                        {...options.ctx.calcRequireIndexProps(input)}
                      >
                        {options.childrenFactory(node)}
                      </Admonition>
                    ),
                  };
                }
                if (nodeName === 'highlight') {
                  const highLightSourceName = (node.name as string)
                    .trim()
                    .split(' ')
                    .filter((item) => Boolean(item));
                  highLightSourceName.shift();
                  const type = highLightSourceName[0];
                  if (!isSupportHighlightType(type as string)) {
                    return {
                      res: null,
                    };
                  }

                  const emojiText = highLightSourceName[1]
                    ? highLightSourceName[1].codePointAt(0)
                    : null;
                  const attributeIcon = node.attributes?.icon;
                  const attributeIconColor = node.attributes?.iconColor ?? undefined;
                  const icon = attributeIcon ? (
                    <span className="item-center inline-flex justify-center">
                      <SupportIconPropsNode icon={attributeIcon} iconColor={attributeIconColor} />
                    </span>
                  ) : null;
                  return {
                    res: (
                      <HighLight
                        emojiText={!icon && emojiText ? String.fromCodePoint(emojiText) : null}
                        customIcon={icon}
                        type={type as any}
                        {...options.ctx.calcRequireIndexProps(input)}
                      >
                        {options.childrenFactory(node)}
                      </HighLight>
                    ),
                  };
                }
                return {
                  res: <div>{options.childrenFactory(node)}</div>,
                };
              }
              return input;
            };
          },
        },
      ]),
      // slug plugin 给 heading 给一个 id
      enableHeadingSlugId
        ? ({
            mdxHandlerList: ({ ctx, childrenFactory, componentMap }) => {
              const calcHeadingText = (nodes: PhrasingContent[]): string => {
                return nodes
                  .map((node) => {
                    if ('children' in node) {
                      return calcHeadingText(node.children as unknown as PhrasingContent[]);
                    }
                    switch (node.type) {
                      case 'text':
                      case 'inlineMath':
                      case 'mdxTextExpression':
                        return node.value;
                      // case "html":
                      // case "imageReference":
                      // case "image":
                      // case "footnoteReference":
                      // case "break":
                      //   return "";
                      default:
                        return '';
                    }
                  })
                  .join('')
                  .trim();
              };
              return (input) => {
                if ('res' in input) return input;
                const node = input.source;
                if (node.type !== 'heading') return input;

                const slug: GithubSlugger = ctx.slugger ?? (ctx.slugger = new GithubSlugger());
                const level = node.depth;
                const Tag = `h${level}` as const;
                const ComponentName = componentMap[Tag] as typeof Tag;

                // rehype-slug
                const id = slug.slug(calcHeadingText(node.children));

                return {
                  res: (
                    <ComponentName id={id} {...ctx.calcRequireIndexProps(input)}>
                      {childrenFactory(node)}
                    </ComponentName>
                  ),
                };
              };
            },
            rehype: (processor) =>
              processor.use(rehypeSlug, {
                prefix: slugIdPrefix,
              }) as unknown as typeof processor,
          } as BytemdSafeMdxViewerPlugin)
        : {},
      // 有 mdx 的 autoLink 已经放在 ComponentMap 中了
      enableHeadingAutoLink ? pluginAutoLinkHeadings() : {},
      pluginImageAddLoading(), // 这个需要使用 SafeMdxViewer 的 customComponent 代理 image 组件渲染
      !!imageReplaceHostOptions
        ? pluginImageSrcPrefix(
            imageReplaceHostOptions.searchHostname,
            imageReplaceHostOptions.replaceHostname,
            imageReplaceHostOptions.customDomainRequestPath,
            imageReplaceHostOptions.isEnterpriseEdition,
          )
        : {},
      enableImageSrcAddParams ? pluginImageSrcAddParams(imageSrcAddParams) : {},
      {
        viewerEffect({ markdownBody }) {
          // icons 显示
          const icons = markdownBody.querySelectorAll<HTMLSpanElement>(
            `span[data-markdown-component="x-admonition-icons"]`,
          );
          setIconElement(
            Array.from(icons).map((v) => {
              return [
                v,
                {
                  icon: v.getAttribute('data-icon') ?? '',
                },
              ] as [
                HTMLElement,
                {
                  icon: string;
                },
              ];
            }),
          );
        },
      },
    ] as BytemdSafeMdxViewerPlugin[];
  }, [
    enableHeadingAutoLink,
    slugIdPrefix,
    enableHeadingSlugId,
    enableImageReplaceHost,
    enableImageSrcAddParams,
    imageReplaceHostOptions?.replaceHostname,
    imageReplaceHostOptions?.searchHostname,
    imageReplaceHostOptions?.customDomainRequestPath,
    imageReplaceHostOptions?.isEnterpriseEdition,
    innerOpenExternalLink,
    locale,
    isSupportHash,
    mermaidPlugin,
  ]);

  const renderMdxError = useMemo(() => {
    if (!showErrorMsg) return () => <></>;
    return (error: string) => (
      <Alert
        style={
          {
            borderRadius: 0,
            '--ui-alert-error-icon-color': 'white',
            '--ui-alert-error-bg-color': 'red',
            '--ui-alert-message-color': 'white',
          } as any
        }
        message={error}
        type="error"
        banner
      />
    );
  }, [showErrorMsg]);

  return (
    <MarkdownSafeMdxViewerAppLowerNameContext.Provider value={appLowerName}>
      <MarkdownSafeMdxViewerLocaleContext.Provider value={locale}>
        <MarkdownOpenExternalLinkCtx.Provider
          value={{
            renderExternalLink,
            openExternalLink: innerOpenExternalLink,
            imageReplaceHostOptions,
            imageSrcAddParams,
            hasBlockExternalComponent,
            ComponentMap: ExternalComponentMap,
            enableHeadingAutoLink,
            slugIdPrefix,
            isSupportHash,
            enableHeadingSlugId,
          }}
        >
          <SafeMdxViewer
            sanitizeFn={sanitizeFn}
            value={viewerValue ?? ''}
            renderMdxError={renderMdxError}
            customComponent={customComponent as any}
            plugins={viewPlugin}
          />
          {iconElement.map((item) => {
            return ReactDOM.createPortal(
              (() => {
                const iconName = item[1].icon;
                if (iconName in AdmonitionConfig) {
                  return AdmonitionConfig[iconName as keyof typeof AdmonitionConfig].icon;
                }
                return <></>;
              })(),
              item[0],
            );
          })}
        </MarkdownOpenExternalLinkCtx.Provider>
      </MarkdownSafeMdxViewerLocaleContext.Provider>
    </MarkdownSafeMdxViewerAppLowerNameContext.Provider>
  );
}

export default Viewer;
